import React, { useState } from "react";
import {
  Form,
  FormGroup,
  InputGroup,
  InputGroupText,
  Input,
  Button,
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
} from "reactstrap";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import PageLayout from "../PageLayout";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { resetPasswordApi } from "src/network/api";

interface PasswordFormData {
  newPassword: string;
  confirmPassword: string;
}

const PasswordReset: React.FC = () => {
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordFormData>({
    mode: "onChange",
  });
  const navigate = useNavigate();
  const { reset_token = null } = useParams();
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleTogglePassword = (type: "new" | "confirm") => {
    if (type === "new") {
      setShowNewPassword(!showNewPassword);
    } else if (type === "confirm") {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  const onSubmit: SubmitHandler<PasswordFormData> = async (data) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.error("New password and confirmation do not match!");
      return;
    }
    if (!reset_token) {
      toast.error("Reset token is missing");
      return;
    }
    try {
      const decodedToken = atob(reset_token);
      setIsProcessing(true);
      let response = await resetPasswordApi(data.newPassword, decodedToken);
      if (response.status == 200 && response.data?.status == 200) {
        toast.success(response.data.message);
        navigate("/");
      } else {
        toast.error(response.data?.message);
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <PageLayout>
      <Container>
        <Row className="justify-content-center mb-4 mt-3">
          <Col lg="6" md="8" sm="10">
            <Card
              className="shadow border-0"
              style={{
                borderRadius: "15px",
                marginTop: "2rem",
              }}
            >
              <CardHeader
                className="text-white text-center"
                style={{
                  backgroundColor: "#5e72e4",
                  borderRadius: "15px 15px 0 0",
                  padding: "1rem",
                }}
              >
                <p style={{ fontSize: "1.5rem", margin: "0" }}>
                  Reset Password
                </p>
              </CardHeader>
              <CardBody>
                <Form role="form" onSubmit={handleSubmit(onSubmit)}>
                  <FormGroup>
                    <InputGroup className="input-group-alternative">
                      <InputGroupText>
                        <i className="ni ni-lock-circle-open text-primary" />
                      </InputGroupText>
                      <Controller
                        name="newPassword"
                        control={control}
                        rules={{
                          required: "New password is required",
                          minLength: {
                            value: 6,
                            message: "Password should have atleast 6 chars.",
                          },
                        }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            placeholder="New Password"
                            type={showNewPassword ? "text" : "password"}
                            style={{ borderRadius: "20px" }}
                          />
                        )}
                      />
                      <InputGroupText
                        onClick={() => handleTogglePassword("new")}
                        style={{ cursor: "pointer" }}
                      >
                        <i
                          className={`fas ${
                            showNewPassword ? "fa-eye-slash" : "fa-eye"
                          }`}
                        />
                      </InputGroupText>
                    </InputGroup>
                    {errors.newPassword && (
                      <div
                        className="error-text"
                        style={{ fontSize: "0.8rem" }}
                      >
                        {String(errors.newPassword?.message)}
                      </div>
                    )}
                  </FormGroup>
                  <FormGroup>
                    <InputGroup className="input-group-alternative">
                      <InputGroupText>
                        <i className="ni ni-lock-circle-open text-primary" />
                      </InputGroupText>
                      <Controller
                        name="confirmPassword"
                        control={control}
                        rules={{
                          required: "Please confirm your new password",
                        }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            placeholder="Confirm New Password"
                            type={showConfirmPassword ? "text" : "password"}
                            style={{ borderRadius: "20px" }}
                          />
                        )}
                      />
                      <InputGroupText
                        onClick={() => handleTogglePassword("confirm")}
                        style={{ cursor: "pointer" }}
                      >
                        <i
                          className={`fas ${
                            showConfirmPassword ? "fa-eye-slash" : "fa-eye"
                          }`}
                        />
                      </InputGroupText>
                    </InputGroup>
                    {errors.confirmPassword && (
                      <div
                        className="error-text"
                        style={{ fontSize: "0.8rem" }}
                      >
                        {String(errors.confirmPassword?.message)}
                      </div>
                    )}
                  </FormGroup>
                  <div className="text-center">
                    <Button
                      className="btn-lg"
                      color="primary"
                      type="submit"
                      disabled={isProcessing}
                      style={{
                        backgroundColor: "#5e72e4",
                        borderRadius: "50px",
                        padding: "0.8rem 2rem",
                        fontSize: "1rem",
                      }}
                    >
                      {isProcessing ? "Please wait..." : "Reset"}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </PageLayout>
  );
};

export default PasswordReset;
